import { useEffect } from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { Trophy, PartyPopper, Heart, X } from "lucide-react";

const L = {
  party: {
    title: { ar: "🎉 مبروووك! حقّقت هدف الشهر 🎉", en: "🎉 Congratulations! Target achieved 🎉", ur: "🎉 مبارک ہو! ہدف مکمل 🎉" },
    body: {
      ar: "أداء بطولي يستحق الاحتفال! شكراً لك على جهدك الكبير وتميّزك هذا الشهر — أنت مصدر فخر لنا 👏🏆",
      en: "A champion performance worth celebrating! Thank you for your outstanding effort this month — we're proud of you 👏🏆",
      ur: "چیمپیئن کارکردگی! اس مہینے آپ کی محنت کا شکریہ — ہمیں آپ پر فخر ہے 👏🏆",
    },
    btn: { ar: "شكراً! 🎊", en: "Thanks! 🎊", ur: "شکریہ! 🎊" },
  },
  thanks: {
    title: { ar: "شكراً لجهدك هذا الشهر 🌟", en: "Thank you for your effort 🌟", ur: "آپ کی محنت کا شکریہ 🌟" },
    body: {
      ar: "كنت قريباً جداً من الهدف! نقدّر مجهودك ونتمنّى لك أداءً أفضل في الأشهر القادمة 💪",
      en: "You were so close to the target! We appreciate your effort and wish you an even better performance next months 💪",
      ur: "آپ ہدف کے بہت قریب تھے! ہم آپ کی محنت کی قدر کرتے ہیں اور اگلے مہینوں میں بہتر کارکردگی کی امید رکھتے ہیں 💪",
    },
    btn: { ar: "حسناً 🙏", en: "OK 🙏", ur: "ٹھیک ہے 🙏" },
  },
};

const fireworks = () => {
  const end = Date.now() + 2500;
  const colors = ["#f59e0b", "#10b981", "#3b82f6", "#ef4444", "#a855f7", "#ffffff"];
  const frame = () => {
    confetti({ particleCount: 6, angle: 60, spread: 70, origin: { x: 0 }, colors });
    confetti({ particleCount: 6, angle: 120, spread: 70, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
  confetti({ particleCount: 160, spread: 100, origin: { y: 0.6 }, colors });
};

export const CelebrationOverlay = ({ type = "party", lang = "ar", onClose }) => {
  const c = L[type] || L.party;
  const pick = (o) => o[lang] || o.ar;
  useEffect(() => {
    if (type === "party") fireworks();
    else confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: ["#10b981", "#3b82f6", "#ffffff"] });
  }, [type]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" dir="rtl" data-testid="celebration-overlay">
      <motion.div
        initial={{ scale: 0.7, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
        className={`relative w-full max-w-md rounded-3xl p-7 text-center text-white shadow-2xl bg-gradient-to-br ${type === "party" ? "from-amber-400 via-orange-500 to-rose-600" : "from-emerald-400 via-teal-500 to-emerald-600"}`}
      >
        <button onClick={onClose} data-testid="celebration-close" className="absolute top-3 left-3 grid h-8 w-8 place-items-center rounded-full bg-white/25 hover:bg-white/40 transition">
          <X className="h-4 w-4" />
        </button>
        <motion.div
          animate={type === "party" ? { rotate: [0, -12, 12, 0], scale: [1, 1.12, 1] } : { scale: [1, 1.08, 1] }}
          transition={{ duration: 1.4, repeat: Infinity }}
          className="mx-auto mb-3 grid h-20 w-20 place-items-center rounded-full bg-white/25 backdrop-blur-sm"
        >
          {type === "party" ? <Trophy className="h-10 w-10 drop-shadow" /> : <Heart className="h-10 w-10 drop-shadow" />}
        </motion.div>
        <h2 className="font-heading text-2xl font-extrabold drop-shadow-md leading-snug" data-testid="celebration-title">{pick(c.title)}</h2>
        <p className="mt-3 text-sm leading-relaxed text-white/95 font-medium">{pick(c.body)}</p>
        <button onClick={onClose} data-testid="celebration-ok-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/90 px-6 py-2.5 font-bold text-slate-800 shadow-lg hover:bg-white transition">
          <PartyPopper className="h-4 w-4" /> {pick(c.btn)}
        </button>
      </motion.div>
    </div>
  );
};
